import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Coins, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import CandidateProfileForm from "@/components/forms/CandidateProfileForm";
import ProducerProfileForm from "@/components/forms/ProducerProfileForm";
import LookupSelect from "@/components/forms/LookupSelect";
import { useLookups } from "@/contexts/LookupsContext";
import api, { formatApiErrorDetail } from "@/lib/api";

function useAdminUsers(role, includeArchived) {
  const [users, setUsers] = useState(null);
  const requestIdRef = useRef(0);
  const reload = () => {
    const params = { include_archived: includeArchived };
    if (role !== "all") params.role = role;
    const myRequestId = ++requestIdRef.current;
    // Guard against out-of-order responses: if the user clicks through filter tabs quickly,
    // an older/slower request could otherwise resolve after a newer one and overwrite the
    // list with the wrong (stale) filter's results.
    api.get("/admin/users", { params }).then(({ data }) => {
      if (myRequestId === requestIdRef.current) setUsers(data);
    });
  };
  useEffect(() => { reload(); }, [role, includeArchived]); // eslint-disable-line
  return [users, reload];
}

function CreateCandidateDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const lookups = useLookups();
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", phone: "", nationality: null });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/candidates", { ...form, origin_url: window.location.origin });
      toast.success(t("admin.createSuccess"));
      setForm({ email: "", first_name: "", last_name: "", phone: "", nationality: null });
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg">
        <DialogHeader><DialogTitle>{t("admin.createCandidateTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("admin.createEmailLabel")}</Label>
            <Input type="email" className="h-11 rounded-xl mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-create-candidate-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("candidateProfile.firstName")}</Label>
              <Input className="h-11 rounded-xl mt-1.5" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="admin-create-candidate-first-name-input" />
            </div>
            <div>
              <Label>{t("candidateProfile.lastName")}</Label>
              <Input className="h-11 rounded-xl mt-1.5" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="admin-create-candidate-last-name-input" />
            </div>
          </div>
          <div>
            <Label>{t("candidateProfile.phone")}</Label>
            <Input className="h-11 rounded-xl mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-create-candidate-phone-input" />
          </div>
          {lookups && (
            <div>
              <Label className="mb-1.5 block">{t("candidateProfile.nationality")}</Label>
              <LookupSelect options={lookups.nationalities} value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })}
                            labelPrefix="lookups.nationalities" testId="admin-create-candidate-nationality-select" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={saving || !form.email.trim()} className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="admin-create-candidate-submit-button">
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CreateProducerDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const lookups = useLookups();
  const [form, setForm] = useState({ email: "", company_name: "", contact_first_name: "", contact_last_name: "", phone: "", country: null });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/producers", { ...form, origin_url: window.location.origin });
      toast.success(t("admin.createSuccess"));
      setForm({ email: "", company_name: "", contact_first_name: "", contact_last_name: "", phone: "", country: null });
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg">
        <DialogHeader><DialogTitle>{t("admin.createProducerTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("admin.createEmailLabel")}</Label>
            <Input type="email" className="h-11 rounded-xl mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-create-producer-email-input" />
          </div>
          <div>
            <Label>{t("producerProfile.companyName")}</Label>
            <Input className="h-11 rounded-xl mt-1.5" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="admin-create-producer-company-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("producerProfile.contactFirstName")}</Label>
              <Input className="h-11 rounded-xl mt-1.5" value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} data-testid="admin-create-producer-contact-first-name-input" />
            </div>
            <div>
              <Label>{t("producerProfile.contactLastName")}</Label>
              <Input className="h-11 rounded-xl mt-1.5" value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} data-testid="admin-create-producer-contact-last-name-input" />
            </div>
          </div>
          <div>
            <Label>{t("producerProfile.phone")}</Label>
            <Input className="h-11 rounded-xl mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-create-producer-phone-input" />
          </div>
          {lookups && (
            <div>
              <Label className="mb-1.5 block">{t("producerProfile.country")}</Label>
              <LookupSelect options={lookups.countries} value={form.country} onChange={(v) => setForm({ ...form, country: v })}
                            labelPrefix="lookups.countries" testId="admin-create-producer-country-select" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={saving || !form.email.trim()} className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="admin-create-producer-submit-button">
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProfileDialog({ user, onOpenChange, onSaved }) {
  const { t } = useTranslation();
  if (!user) return null;
  const isCandidate = user.role === "candidate";
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isCandidate ? t("admin.editCandidateTitle") : t("admin.editProducerTitle")}</DialogTitle></DialogHeader>
        {isCandidate ? (
          <CandidateProfileForm fetchUrl={`/admin/candidates/${user.id}`} saveUrl={`/admin/candidates/${user.id}`} showUploads={false} onSaved={onSaved} />
        ) : (
          <ProducerProfileForm fetchUrl={`/admin/producers/${user.id}`} saveUrl={`/admin/producers/${user.id}`} showLogoUpload={false} onSaved={onSaved} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreditsDialog({ user, onOpenChange, onGranted }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(5);
  const [saving, setSaving] = useState(false);

  const grant = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/producers/${user.id}/credits`, { amount: Number(amount) });
      toast.success(t("common.success"));
      onOpenChange(false);
      onGranted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-sm">
        <DialogHeader><DialogTitle>{t("admin.creditsDialogTitle")}</DialogTitle></DialogHeader>
        <div>
          <Label>{t("admin.creditsAmountLabel")}</Label>
          <Input type="number" className="h-11 rounded-xl mt-1.5" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="admin-credits-amount-input" />
        </div>
        <DialogFooter>
          <Button onClick={grant} disabled={saving || !amount} className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="admin-credits-submit-button">
            {saving ? t("common.loading") : t("admin.creditsAdd")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirmDialog({ open, onOpenChange, title, description, onConfirm, confirming, testId }) {
  const { t } = useTranslation();
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction disabled={confirming} onClick={onConfirm} className="bg-[#FF3B30] hover:bg-[#D9291F]" data-testid={`${testId}-confirm`}>
            {confirming ? t("common.loading") : t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UserRow({ u, index, onAction, onEdit, onCredits, onDeleteRequest, selected, onToggleSelect, shiftHeldRef }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between bg-white rounded-xl border border-black/5 p-4 text-sm gap-3 flex-wrap" data-testid={`admin-user-row-${u.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Checkbox checked={selected} onMouseDown={(e) => { shiftHeldRef.current = e.shiftKey; }}
                  onCheckedChange={() => onToggleSelect(u.id, index)} data-testid={`admin-user-select-${u.id}`} />
        <div className="min-w-0">
          <p className="font-semibold truncate">{u.display_name || u.email}</p>
          <p className="text-xs text-[#2B2B2B]/50 truncate">{u.email} &middot; {u.role}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {u.is_complete !== null && (
          <Badge className={u.is_complete ? "bg-[#34C759]/10 text-[#34C759] border-0" : "bg-[#dde165]/20 text-[#2B2B2B] border-0"}>
            {u.is_complete ? t("candidateProfile.profileComplete") : t("candidateProfile.profileIncomplete")}
          </Badge>
        )}
        {u.offers_count !== null && <Badge variant="outline">{u.offers_count} {t("admin.tabOffersAdmin")}</Badge>}
        {u.is_archived && <Badge className="bg-black/10 border-0">{t("admin.archivedBadge")}</Badge>}
        <Button size="sm" variant="ghost" title={t("common.edit")} onClick={() => onEdit(u)} data-testid={`admin-edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
        {u.role === "producer" && (
          <Button size="sm" variant="ghost" title={t("admin.creditsDialogTitle")} onClick={() => onCredits(u)} data-testid={`admin-credits-user-${u.id}`}><Coins className="h-4 w-4" /></Button>
        )}
        {!u.is_verified && <Button size="sm" variant="outline" onClick={() => onAction(u.id, "verify")} data-testid={`admin-verify-user-${u.id}`}>{t("admin.verifyAccount")}</Button>}
        {u.is_blocked ? (
          <Button size="sm" variant="outline" onClick={() => onAction(u.id, "unblock")} data-testid={`admin-unblock-user-${u.id}`}>{t("admin.unblockAccount")}</Button>
        ) : (
          <Button size="sm" variant="ghost" className="text-[#FF3B30]" onClick={() => onAction(u.id, "block")} data-testid={`admin-block-user-${u.id}`}>{t("admin.blockAccount")}</Button>
        )}
        {u.is_archived ? (
          <Button size="sm" variant="ghost" title={t("admin.unarchiveAccount")} onClick={() => onAction(u.id, "unarchive")} data-testid={`admin-unarchive-user-${u.id}`}><ArchiveRestore className="h-4 w-4" /></Button>
        ) : (
          <Button size="sm" variant="ghost" className="text-[#FF3B30]" title={t("admin.archiveAccount")} onClick={() => onAction(u.id, "archive")} data-testid={`admin-archive-user-${u.id}`}><Archive className="h-4 w-4" /></Button>
        )}
        <Button size="sm" variant="ghost" className="text-[#FF3B30]" title={t("admin.deleteAccount")} onClick={() => onDeleteRequest(u)} data-testid={`admin-delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function BulkUserToolbar({ selectedIds, onDone }) {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const runAction = async (action) => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/users/bulk-action", { user_ids: [...selectedIds], action });
      toast.success(`${data.modified_count} ${t("admin.bulkActionApplied")}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setRunning(false);
    }
  };

  const runBulkDelete = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: [...selectedIds] });
      toast.success(`${data.deleted_count} ${t("admin.bulkDeleteApplied")}`);
      setConfirmDeleteOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap bg-[#2B2B2B] text-white rounded-xl p-3" data-testid="admin-users-bulk-toolbar">
      <span className="text-sm font-medium px-2">{selectedIds.size} {t("admin.selected")}</span>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("verify")} data-testid="admin-bulk-verify-button">{t("admin.verifyAccount")}</Button>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("block")} data-testid="admin-bulk-block-button">{t("admin.blockAccount")}</Button>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("unblock")} data-testid="admin-bulk-unblock-button">{t("admin.unblockAccount")}</Button>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("archive")} data-testid="admin-bulk-archive-button">{t("admin.archiveAccount")}</Button>
      <Button size="sm" variant="secondary" disabled={running} onClick={() => runAction("unarchive")} data-testid="admin-bulk-unarchive-button">{t("admin.unarchiveAccount")}</Button>
      <Button size="sm" variant="destructive" disabled={running} onClick={() => setConfirmDeleteOpen(true)} data-testid="admin-bulk-delete-button">
        <Trash2 className="h-3.5 w-3.5 mr-1" /> {t("admin.bulkDeleteButton")}
      </Button>
      <DeleteConfirmDialog
        open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen} confirming={running}
        title={t("admin.bulkDeleteConfirmTitle", { count: selectedIds.size })}
        description={t("admin.bulkDeleteConfirmDesc")}
        onConfirm={runBulkDelete} testId="admin-bulk-delete-confirm-dialog"
      />
    </div>
  );
}

export default function UsersPanel() {
  const { t } = useTranslation();
  const [role, setRole] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [users, reload] = useAdminUsers(role, includeArchived);
  const [editingUser, setEditingUser] = useState(null);
  const [creditsUser, setCreditsUser] = useState(null);
  const [createCandidateOpen, setCreateCandidateOpen] = useState(false);
  const [createProducerOpen, setCreateProducerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const shiftHeldRef = useRef(false);
  const lastIndexRef = useRef(null);

  useEffect(() => { setSelectedIds(new Set()); lastIndexRef.current = null; }, [role, includeArchived]);

  const toggleSelect = (id, index) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftHeldRef.current && lastIndexRef.current !== null && users) {
        const start = Math.min(lastIndexRef.current, index);
        const end = Math.max(lastIndexRef.current, index);
        for (let i = start; i <= end; i++) next.add(users[i].id);
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastIndexRef.current = index;
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === users.length ? new Set() : new Set(users.map((u) => u.id))));
  };

  const act = async (id, action) => {
    try { await api.post(`/admin/users/${id}/${action}`); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail, t)); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(t("common.success"));
      setDeleteTarget(null);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(deleteTarget.id); return next; });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "producer", "candidate"].map((r) => (
            <Button key={r} size="sm" variant={role === r ? "default" : "outline"}
                    className={role === r ? "bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" : "rounded-xl"}
                    onClick={() => setRole(r)} data-testid={`admin-users-filter-${r}`}>
              {t(`admin.filter${r === "all" ? "All" : r === "producer" ? "Producers" : "Candidates"}`)}
            </Button>
          ))}
          <Button size="sm" variant={includeArchived ? "default" : "outline"}
                  className={includeArchived ? "bg-[#2B2B2B] rounded-xl" : "rounded-xl"}
                  onClick={() => setIncludeArchived((v) => !v)} data-testid="admin-users-toggle-archived">
            {t("admin.showArchived")}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={() => setCreateCandidateOpen(true)} data-testid="admin-open-create-candidate">
            <Plus className="h-3.5 w-3.5" /> {t("admin.newCandidate")}
          </Button>
          <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={() => setCreateProducerOpen(true)} data-testid="admin-open-create-producer">
            <Plus className="h-3.5 w-3.5" /> {t("admin.newProducer")}
          </Button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <BulkUserToolbar selectedIds={selectedIds} onDone={() => { setSelectedIds(new Set()); reload(); }} />
      )}

      {!users ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7] mx-auto" />
      ) : (
        <div className="space-y-2">
          {users.length > 0 && (
            <div className="flex items-center gap-2 px-4 text-xs text-[#2B2B2B]/50">
              <Checkbox checked={selectedIds.size === users.length} onCheckedChange={toggleSelectAll} data-testid="admin-users-select-all" />
              {t("admin.selectAll")}
            </div>
          )}
          {users.map((u, index) => (
            <UserRow key={u.id} u={u} index={index} onAction={act} onEdit={setEditingUser} onCredits={setCreditsUser}
                     onDeleteRequest={setDeleteTarget} selected={selectedIds.has(u.id)} onToggleSelect={toggleSelect}
                     shiftHeldRef={shiftHeldRef} />
          ))}
        </div>
      )}

      <CreateCandidateDialog open={createCandidateOpen} onOpenChange={setCreateCandidateOpen} onCreated={reload} />
      <CreateProducerDialog open={createProducerOpen} onOpenChange={setCreateProducerOpen} onCreated={reload} />
      <EditProfileDialog user={editingUser} onOpenChange={(v) => !v && setEditingUser(null)} onSaved={reload} />
      {creditsUser && <CreditsDialog user={creditsUser} onOpenChange={(v) => !v && setCreditsUser(null)} onGranted={reload} />}
      <DeleteConfirmDialog
        open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)} confirming={deleting}
        title={t("admin.deleteAccountConfirmTitle")}
        description={`${deleteTarget?.display_name || deleteTarget?.email || ""} — ${t("admin.deleteAccountConfirmDesc")}`}
        onConfirm={confirmDelete} testId="admin-delete-user-confirm-dialog"
      />
    </div>
  );
}
